import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EndRecommendCard } from '~/components/end-recommend-card';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ClickPosition, ClickPositionPrefix } from '~/utils/click-position';
import { ModuleType } from '~/utils/module-type';
import { Button, ButtonSize } from '@lemon8/design';
import ArrowClockwise from 'icon/arrow_clockwise.svg';
import { useActiveToFeedForyou } from '~/utils/active/use-active-to-feed';
import useReflowClick from '~/utils/use-reflow-click';
import { IArticleRelatedPost } from 'shared/atoms';
import { TArticleDetail } from 'shared/models';
import { Loading } from '~/components/loading';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { useAtomValue, useSetAtom } from 'jotai';
import { $SimpleCTAModalShow } from '~/models/portal/simple-cta-modal';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';
import { AdditionalParamsOptionalProps, PositionPrefixOptionalProps } from '../immersive-post-item/shared/typing';

import './style.scss';

interface IVideoEndRecommendProps {
  show: boolean;
  article: TArticleDetail;
  relatedPost: IArticleRelatedPost;
  onClickReplay: () => void;
}

const VideoEndRecommendNoContent: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="video-end-recommend-empty">
      <span>{t('no_content')}</span>
    </div>
  );
};

const VideoEndRecommendLoading: FC = () => {
  return (
    <div className="video-end-recommend-loading">
      <Loading variant="ball" />
    </div>
  );
};

export const VideoEndRecommend: FC<
  AdditionalParamsOptionalProps & PositionPrefixOptionalProps & IVideoEndRecommendProps
> = (props) => {
  const { t } = useTranslation();
  const activeToFeedForyou = useActiveToFeedForyou();

  const {
    show,
    relatedPost,
    onClickReplay,
    article,
    positionPrefix = ClickPositionPrefix.none,
    additionalParams = {},
  } = props;
  const isMobileUser = useAtomValue($IsMobileUser);
  const setSimpleCTAModal = useSetAtom($SimpleCTAModalShow);
  const activeToCurrentArticle = useActiveToArticle(article);

  const { popupContent = { description: '', buttonOpen: '' } } = article;

  const observedRef = useViewTracking(
    {
      onView: useCallback(() => {
        window.Tea('wap_module_show', {
          current_page_module: positionPrefix + ModuleType.main_related_post,
          group_id: article.groupId,
          ...additionalParams,
        });
      }, []),
    },
    [show],
  );

  const mainSeeMoreRef = useViewTracking(
    {
      onView: useCallback(() => {
        window.Tea('wap_cta_show', {
          current_position: positionPrefix + ClickPosition.main_see_more_btn,
          current_page_module: positionPrefix + ModuleType.main_related_post,
          group_id: article.groupId,
          ...additionalParams,
        });
      }, []),
    },
    [show],
  );

  const handleEndRecommendBtnClick = useReflowClick(
    {
      clickEvent: () => ({
        current_position: positionPrefix + ClickPosition.main_see_more_btn,
        is_cta: 1,
        ...additionalParams,
      }),
      reflowBehavior: () => {
        activeToFeedForyou([positionPrefix, ClickPosition.main_see_more_btn]);
      },
      defaultBehavior: () => {
        activeToFeedForyou([positionPrefix, ClickPosition.main_see_more_btn]);
      },
    },
    [activeToFeedForyou],
  );

  const replayBtnRef = useViewTracking(
    {
      onView: useCallback(() => {
        window.Tea('wap_cta_show', {
          current_position: positionPrefix + ClickPosition.main_video_replay,
          current_page_module: positionPrefix + ModuleType.main_related_post,
          ...additionalParams,
        });
      }, []),
    },
    [show],
  );

  const handleReplayBtnClick = useCallback(() => {
    window.Tea('wap_click', {
      current_position: positionPrefix + ClickPosition.main_video_replay,
      ...additionalParams,
    });
    if (isMobileUser) {
      setSimpleCTAModal({
        body: t('seo_stopVideo_modal_desc'),
        currentPosition: [positionPrefix, ClickPosition.main_video_replay_modal],
        handleClickConfirm: () => activeToCurrentArticle([positionPrefix, ClickPosition.main_video_replay_modal]),
        popupContent,
        additionalParams,
      });
    } else {
      onClickReplay();
    }
  }, [onClickReplay]);

  const itemsToShow = useMemo(() => {
    const { items } = relatedPost;

    return items.filter((item) => item.groupId !== article.groupId).slice(0, 5);
  }, [relatedPost, article]);

  if (!show) {
    return null;
  }

  return (
    <div className="video-end-recommend" ref={observedRef}>
      <p className="video-end-recommend-title">{t('seo_v3_postDetail_recommended_title')}</p>
      <div className="video-end-recommend-main">
        {!relatedPost.items.length && relatedPost.hasMore && <VideoEndRecommendLoading />}
        {!relatedPost.items.length && !relatedPost.hasMore && <VideoEndRecommendNoContent />}
        {Boolean(relatedPost.items.length) && (
          <Swiper resistanceRatio={0} slidesPerView={'auto'} nested>
            {itemsToShow.map((item, index) => (
              <SwiperSlide key={item.groupId}>
                <EndRecommendCard article={item} size="normal" rank={index} positionPrefix={positionPrefix} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
      <div className="active-btn" ref={mainSeeMoreRef}>
        <Button
          variant="Secondary"
          size={ButtonSize.large}
          onClick={handleEndRecommendBtnClick}
          title={t('seo_v3_postDetail_openApp_btn')}
        />
      </div>
      <div className="replay-btn" ref={replayBtnRef} onClick={handleReplayBtnClick}>
        <ArrowClockwise />
        <span>{t('seo_v3_postDetail_replay_btn')}</span>
      </div>
    </div>
  );
};
